import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import EngagementsSection from '../components/EngagementsSection';
import { formatCurrency, formatDateTime, STAGE_DISPLAY_NAMES } from '../lib/utils';
import {
  ArrowLeft, Building2, Users, Briefcase, DollarSign, Clock, TrendingUp,
  Calendar, Mail, Phone, FileText, MessageSquare, ChevronRight, Loader2,
  Pencil, Globe, MapPin, Plus, UserPlus, Search, X, Check,
} from 'lucide-react';

const ACTIVITY_ICONS = {
  note: MessageSquare, call: Phone, meeting: Users, email: Mail, email_sent: Mail,
  stage_change: TrendingUp, created: FileText, deck_delivered: FileText,
  opportunity_created: TrendingUp, followup_set: Calendar, organization_changed: Building2,
};
const ACTIVITY_COLORS = {
  note: 'bg-blue-100 text-blue-700', call: 'bg-green-100 text-green-700',
  meeting: 'bg-purple-100 text-purple-700', email: 'bg-orange-100 text-orange-700',
  email_sent: 'bg-orange-100 text-orange-700', stage_change: 'bg-indigo-100 text-indigo-700',
  created: 'bg-gray-100 text-gray-700', deck_delivered: 'bg-amber-100 text-amber-700',
  opportunity_created: 'bg-emerald-100 text-emerald-700', followup_set: 'bg-cyan-100 text-cyan-700',
  organization_changed: 'bg-violet-100 text-violet-700',
};

export default function OrganizationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { get, put, post, del } = useApi();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Add Contact flow
  const [addChoiceOpen, setAddChoiceOpen] = useState(false);
  const [addExistingOpen, setAddExistingOpen] = useState(false);
  const [contactQuery, setContactQuery] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [associating, setAssociating] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const d = await get(`/organizations/${id}`);
      setData(d);
    } catch (e) {
      console.error('Failed to load organization:', e);
      toast.error('Organization not found');
      navigate('/organizations');
    } finally {
      setLoading(false);
    }
  }, [get, id, navigate]);

  useEffect(() => { fetchData(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const searchContacts = useCallback(async (q) => {
    setSearching(true);
    try {
      const res = await get('/contacts', q ? { search: q } : undefined);
      // Exclude contacts already linked to THIS organization.
      const linkedIds = new Set((data?.contacts || []).map((c) => c.id));
      setContactResults((res || []).filter((c) => !linkedIds.has(c.id)));
    } catch (e) {
      console.error('contact search failed', e);
    } finally {
      setSearching(false);
    }
  }, [get, data]);

  useEffect(() => {
    if (!addExistingOpen) return;
    const t = setTimeout(() => searchContacts(contactQuery), 250);
    return () => clearTimeout(t);
  }, [contactQuery, addExistingOpen, searchContacts]);

  if (loading || !data) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const { organization: org, stats, contacts, opportunities, timeline } = data;
  const canEdit = org.editable && user?.role !== 'executive_viewer';

  const openEdit = () => {
    setForm({
      name: org.name || '', organization_type: org.organization_type || '',
      industry: org.industry || '', website: org.website || '', phone: org.phone || '',
      address: org.address || '', status: org.status || '', notes: org.notes || '',
    });
    setEditOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await put(`/organizations/${id}`, form);
      toast.success('Organization updated');
      setEditOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const renderActivityIcon = (type) => {
    const Icon = ACTIVITY_ICONS[type] || MessageSquare;
    return <Icon className="h-4 w-4" />;
  };

  const openCreateNewContact = () => {
    setAddChoiceOpen(false);
    navigate(
      `/contacts?create=true&account_id=${encodeURIComponent(id)}&account_name=${encodeURIComponent(org.name)}`
    );
  };

  const openAddExisting = () => {
    setAddChoiceOpen(false);
    setContactQuery('');
    setContactResults([]);
    setAddExistingOpen(true);
  };

  const handleAssociate = async (contactId) => {
    setAssociating(true);
    try {
      await post(`/organizations/${id}/contacts`, { contact_id: contactId });
      toast.success('Contact added to organization');
      setAddExistingOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAssociating(false);
    }
  };

  const handleUnlink = async (contactId) => {
    if (!window.confirm('Remove this contact from the organization? The contact itself is kept.')) return;
    try {
      await del(`/organizations/${id}/contacts/${contactId}`);
      toast.success('Contact removed from organization');
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="organization-detail-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-sm">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-serif text-2xl sm:text-3xl flex items-center gap-2">
                <Building2 className="h-6 w-6" />
                {org.name}
              </h1>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {org.organization_type && (
                  <Badge variant="outline" className="rounded-sm text-xs">{org.organization_type}</Badge>
                )}
                {org.industry && (
                  <Badge variant="secondary" className="rounded-sm text-xs">{org.industry}</Badge>
                )}
                {org.status && (
                  <Badge variant="outline" className="rounded-sm text-xs capitalize">{org.status}</Badge>
                )}
                {org.is_legacy && (
                  <Badge variant="secondary" className="rounded-sm text-xs">From Opportunity</Badge>
                )}
              </div>
            </div>
          </div>
          {canEdit && (
            <Button variant="outline" className="rounded-sm" onClick={openEdit} data-testid="edit-organization-btn">
              <Pencil className="h-4 w-4 mr-2" /> Edit
            </Button>
          )}
        </div>

        {/* Profile line (website / phone / address) */}
        {(org.website || org.phone || org.address) && (
          <div className="flex items-center gap-4 flex-wrap text-sm text-muted-foreground">
            {org.website && (
              <span className="flex items-center gap-1.5"><Globe className="h-4 w-4" />{org.website}</span>
            )}
            {org.phone && (
              <span className="flex items-center gap-1.5"><Phone className="h-4 w-4" />{org.phone}</span>
            )}
            {org.address && (
              <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{org.address}</span>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="rounded-sm border"><CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1"><DollarSign className="h-4 w-4" /><span className="text-xs">Total Pipeline</span></div>
            <p className="text-xl font-semibold">{formatCurrency(stats.total_pipeline_value)}</p>
          </CardContent></Card>
          <Card className="rounded-sm border"><CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1"><Briefcase className="h-4 w-4" /><span className="text-xs">Active Opps</span></div>
            <p className="text-xl font-semibold">{stats.active_opportunities}</p>
          </CardContent></Card>
          <Card className="rounded-sm border"><CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1"><TrendingUp className="h-4 w-4" /><span className="text-xs">Closed Won</span></div>
            <p className="text-xl font-semibold text-emerald-600">{stats.closed_won}</p>
          </CardContent></Card>
          <Card className="rounded-sm border"><CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1"><Briefcase className="h-4 w-4" /><span className="text-xs">Total Opps</span></div>
            <p className="text-xl font-semibold">{stats.total_opportunities}</p>
          </CardContent></Card>
          <Card className="rounded-sm border"><CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1"><Users className="h-4 w-4" /><span className="text-xs">Contacts</span></div>
            <p className="text-xl font-semibold">{stats.total_contacts}</p>
          </CardContent></Card>
          <Card className="rounded-sm border"><CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1"><Clock className="h-4 w-4" /><span className="text-xs">Last Activity</span></div>
            <p className="text-sm font-medium">{stats.last_activity_date ? formatDateTime(stats.last_activity_date) : 'N/A'}</p>
          </CardContent></Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="timeline" className="w-full">
          <TabsList className="grid w-full grid-cols-3 rounded-sm">
            <TabsTrigger value="timeline" className="rounded-sm" data-testid="org-timeline-tab">
              <MessageSquare className="h-4 w-4 mr-2" /> Timeline
            </TabsTrigger>
            <TabsTrigger value="contacts" className="rounded-sm" data-testid="org-contacts-tab">
              <Users className="h-4 w-4 mr-2" /> Contacts ({contacts.length})
            </TabsTrigger>
            <TabsTrigger value="opportunities" className="rounded-sm" data-testid="org-opps-tab">
              <Briefcase className="h-4 w-4 mr-2" /> Opportunities ({opportunities.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="timeline" className="mt-4">
            <Card className="rounded-sm border">
              <CardHeader className="pb-4"><CardTitle className="text-base font-medium">Activity Timeline ({timeline.length})</CardTitle></CardHeader>
              <CardContent>
                {timeline.length === 0 ? (
                  <div className="text-center py-8"><MessageSquare className="h-10 w-10 mx-auto mb-3 text-muted-foreground" /><p className="text-muted-foreground">No activity yet</p></div>
                ) : (
                  <div className="space-y-0">
                    {timeline.map((a, idx) => (
                      <div key={a.id || idx} className="flex gap-4 relative">
                        {idx < timeline.length - 1 && <div className="absolute left-5 top-10 bottom-0 w-px bg-border" />}
                        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${ACTIVITY_COLORS[a.type] || 'bg-gray-100 text-gray-700'} z-10`}>{renderActivityIcon(a.type)}</div>
                        <div className="flex-1 pb-6">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className="text-xs rounded-sm capitalize">{(a.type || '').replace('_', ' ')}</Badge>
                                {a.entity_name && <span className="text-xs text-muted-foreground">• {a.entity_name}</span>}
                              </div>
                              <p className="text-sm">{a.content}</p>
                            </div>
                            <div className="text-right text-xs text-muted-foreground whitespace-nowrap">
                              <p>{formatDateTime(a.created_at)}</p>
                              <p className="mt-0.5">{a.created_by_name}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="contacts" className="mt-4">
            <Card className="rounded-sm border">
              <CardHeader className="pb-4 flex flex-row items-center justify-between">
                <CardTitle className="text-base font-medium">Contacts ({contacts.length})</CardTitle>
                {user?.role !== 'executive_viewer' && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-sm"
                    onClick={() => setAddChoiceOpen(true)}
                    data-testid="org-add-contact-btn"
                  >
                    <Plus className="h-4 w-4 mr-1.5" /> Add Contact
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {contacts.length === 0 ? (
                  <div className="text-center py-8"><Users className="h-10 w-10 mx-auto mb-3 text-muted-foreground" /><p className="text-muted-foreground">No contacts linked</p></div>
                ) : (
                  <div className="space-y-2">
                    {contacts.map((c) => (
                      <div key={c.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-sm hover:bg-muted/50 transition-colors group" data-testid={`org-contact-row-${c.id}`}>
                        <Link to={`/contacts/${c.id}`} className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-medium text-primary">{(c.first_name || ' ')[0]}{(c.last_name || ' ')[0]}</span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate">{c.first_name} {c.last_name}</p>
                            <p className="text-xs text-muted-foreground truncate">{c.email}</p>
                          </div>
                        </Link>
                        {user?.role !== 'executive_viewer' && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 rounded-sm text-muted-foreground hover:text-destructive flex-shrink-0"
                            onClick={() => handleUnlink(c.id)}
                            title="Remove from organization"
                            data-testid={`org-contact-unlink-${c.id}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="opportunities" className="mt-4">
            <Card className="rounded-sm border">
              <CardHeader className="pb-4"><CardTitle className="text-base font-medium">Opportunities ({opportunities.length})</CardTitle></CardHeader>
              <CardContent>
                {opportunities.length === 0 ? (
                  <div className="text-center py-8"><Briefcase className="h-10 w-10 mx-auto mb-3 text-muted-foreground" /><p className="text-muted-foreground">No opportunities</p></div>
                ) : (
                  <div className="space-y-2">
                    {opportunities.map((opp) => (
                      <Link key={opp.id} to={`/opportunity/${opp.id}`} className="flex items-center justify-between p-3 bg-muted/30 rounded-sm hover:bg-muted/50 transition-colors group">
                        <div>
                          <p className="font-medium">{opp.company_name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="rounded-sm text-xs">{STAGE_DISPLAY_NAMES[opp.stage] || opp.stage}</Badge>
                            <span className="text-xs text-muted-foreground">{opp.probability}% probability</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-medium">{formatCurrency(opp.deal_value)}</span>
                          <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Engagements linked to this organization */}
        <EngagementsSection scope="organization" entityId={id} entityName={org.name} canEdit={user?.role !== 'executive_viewer'} />
      </div>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader><DialogTitle>Edit Organization</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="organization-name-edit-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Type</Label>
                <Input value={form.organization_type} onChange={(e) => setForm({ ...form, organization_type: e.target.value })} className="rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Industry</Label>
                <Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="rounded-sm" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Website</Label>
                <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Status</Label>
              <Input value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="rounded-sm" data-testid="save-org-btn">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Contact — choice dialog */}
      <Dialog open={addChoiceOpen} onOpenChange={setAddChoiceOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader><DialogTitle>Add Contact to {org.name}</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <button
              onClick={openCreateNewContact}
              className="flex items-center gap-3 p-4 rounded-sm border hover:border-primary/50 hover:bg-muted/40 transition-all text-left"
              data-testid="org-create-new-contact"
            >
              <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center flex-shrink-0">
                <UserPlus className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">Create New Contact</p>
                <p className="text-xs text-muted-foreground">Open the new-contact form with this organization pre-selected</p>
              </div>
            </button>
            <button
              onClick={openAddExisting}
              className="flex items-center gap-3 p-4 rounded-sm border hover:border-primary/50 hover:bg-muted/40 transition-all text-left"
              data-testid="org-add-existing-contact"
            >
              <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">Add Existing Contact</p>
                <p className="text-xs text-muted-foreground">Search your contacts and link one to this organization</p>
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Existing Contact — search + select */}
      <Dialog open={addExistingOpen} onOpenChange={setAddExistingOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader><DialogTitle>Add Existing Contact</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                autoFocus
                placeholder="Search contacts by name or email..."
                value={contactQuery}
                onChange={(e) => setContactQuery(e.target.value)}
                className="pl-9 rounded-sm"
                data-testid="org-existing-contact-search"
              />
            </div>
            <div className="max-h-[320px] overflow-y-auto space-y-1" data-testid="org-existing-contact-results">
              {searching ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : contactResults.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No contacts found</p>
              ) : (
                contactResults.map((c) => (
                  <button
                    key={c.id}
                    disabled={associating}
                    onClick={() => handleAssociate(c.id)}
                    className="w-full flex items-center justify-between p-3 rounded-sm hover:bg-muted/50 transition-colors text-left disabled:opacity-50"
                    data-testid={`org-existing-contact-option-${c.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-medium text-primary">{(c.first_name || ' ')[0]}{(c.last_name || ' ')[0]}</span>
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{c.first_name} {c.last_name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {c.email}{c.account_name ? ` • ${c.account_name}` : ''}
                        </p>
                      </div>
                    </div>
                    <Check className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  </button>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
